/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import CropModal from "../../Components/Common/CropModal";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

//Import Breadcrumb
import BreadCrumb from "../../Components/Common/BreadCrumb";
import dummyImg from "../../assets/images/users/user-dummy-img.jpg";
import TableContainer from "../../Components/Common/TableContainer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useProfile } from "../../Components/Hooks/UserHooks";
import {
  createSubscriptionBanner,
  deleteSubscriptionBanner,
  listSubscriptionBanner,
  updateBannerStatus,
  updateSubscriptionBanner,
} from "./_requests";

const SubscriptionBanner = () => {
  const { userProfile } = useProfile();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [banners, setBanners] = useState<any>([]);
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [bannerToDeleteId, setBannerToDeleteId] = useState<string>("");
  const [previewImage, setPreviewImage] = useState<string | null>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dimensionError, setDimensionError] = useState<string | null>(null);

  // Cropper states
  const [cropModal, setCropModal] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);

  function formatBytes(bytes: any, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const toggle = useCallback(() => {
    setModal(!modal);
  }, [modal]);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModalOpen(!deleteModalOpen);
  }, [deleteModalOpen]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImageToCrop(e.target.result as string);
          setCropModal(true);
        }
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    }
  };

  const handleCropSave = (croppedFile: File) => {
    setSelectedFile(croppedFile);
    validation.setFieldValue("image", croppedFile);
    setPreviewImage(URL.createObjectURL(croppedFile));
    setDimensionError(null);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await listSubscriptionBanner();
      setBanners(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch banners");
    }
  };

  const onClickDelete = useCallback(
    (banner: any) => {
      setBannerToDeleteId(banner.id);
      toggleDeleteModal();
    },
    [toggleDeleteModal]
  );

  const confirmDelete = async (bannerId: any) => {
    try {
      const res = await deleteSubscriptionBanner(bannerId, userProfile?.userId || 0);
      toast.success(res.data?.message || "Deleted successfully", { autoClose: 3000 });
      fetchBanners();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete banner");
    }
    toggleDeleteModal();
  };

  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
  const [pendingStatusData, setPendingStatusData] = useState<any>(null);

  const toggleStatusModal = useCallback(() => {
    setStatusModalOpen(!statusModalOpen);
  }, [statusModalOpen]);

  const handleStatusToggle = (banner: any) => {
    const newStatus = !banner.enable_disable_status;
    const action = newStatus ? "Activate" : "Deactivate";
    setPendingStatusData({ id: banner.id, newStatus, action });
    toggleStatusModal();
  };

  const confirmStatusUpdate = () => {
    if (pendingStatusData) {
      const { id, newStatus, action } = pendingStatusData;
      updateBannerStatus(id, { enable_disable_status: newStatus }).then((res: any) => {
        if (res && res.status) {
          toast.success(`Banner ${action}d successfully`);
          fetchBanners();
        } else {
          toast.error((res && res.message) || "Failed to update status");
        }
      }).catch((err) => {
        console.error(err);
        toast.error("An error occurred while updating status");
      });
    }
    toggleStatusModal();
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      image: "",
    },
    validationSchema: Yup.object().shape({
      image: Yup.mixed().test(
        "image-required",
        "Image is required",
        function (value) {
          if (!isEdit || !previewImage) {
            return !!value;
          }
          return true;
        }
      ),
    }),
    onSubmit: async (values: any) => {
      try {
        const formData = new FormData();
        if (values.image) {
          formData.append("image", values.image);
        }
        formData.append(isEdit ? "updated_by" : "created_by", userProfile?.userId || "0");

        const actionPromise = isEdit
          ? updateSubscriptionBanner(formData, values.id)
          : createSubscriptionBanner(formData);

        const res = await actionPromise;

        toast.success(res.data?.message || "Success", { autoClose: 3000 });
        fetchBanners();
        validation.resetForm();
        toggle();
      } catch (error: any) {
        console.error("Error:", error);
        toast.error("Something went wrong", { autoClose: 3000 });
      }
    },
  });

  const handleBannerClick = useCallback(
    (banner: any) => {
      validation.setValues({
        id: banner.id,
        image: "",
      });
      setPreviewImage(banner.image);
      setSelectedFile(null);
      setDimensionError(null);
      setIsEdit(true);
      toggle();
    },
    [toggle, validation]
  );

  const columns = useMemo(
    () => [
      {
        header: "S.No",
        id: "sno",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const { pageIndex, pageSize } = cellProps.table.getState().pagination;
          return pageIndex * pageSize + cellProps.row.index + 1;
        },
      },
      {
        header: "Banner Image",
        accessorKey: "image",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="avatar-lg bg-light rounded p-1">
            <img
              src={cell.row.original.image || dummyImg}
              alt=""
              className="img-fluid d-block h-100 object-fit-contain"
            />
          </div>
        ),
      },
      {
        header: "Last Updated",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = new Date(cell.getValue());
          return date.toLocaleDateString() + " " + date.toLocaleTimeString();
        }
      },
      {
        header: "Status",
        accessorKey: "enable_disable_status",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={cell.getValue()}
              onChange={() => handleStatusToggle(cell.row.original)}
            />
          </div>
        ),
      },
      {
        header: "Action",
        cell: (cellProps: any) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item edit" title="Edit">
                <Link
                  to="#"
                  className="text-primary d-inline-block edit-item-btn"
                  onClick={() => handleBannerClick(cellProps.row.original)}
                >
                  <i className="ri-pencil-fill fs-16"></i>
                </Link>
              </li>
              <li className="list-inline-item" title="Remove">
                <Link
                  to="#"
                  className="text-danger d-inline-block remove-item-btn"
                  onClick={() => onClickDelete(cellProps.row.original)}
                >
                  <i className="ri-delete-bin-5-fill fs-16"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleBannerClick, onClickDelete, handleStatusToggle]
  );

  const addBanner = () => {
    setIsEdit(false);
    toggle();
    validation.resetForm({
      values: {
        id: "",
        image: "",
      },
    });
    setPreviewImage("");
    setSelectedFile(null);
    setDimensionError(null);
  };

  document.title = "Subscription Banner | Paal Dappa Admin";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Subscription Banner" pageTitle="Dashboard" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">Subscription Banner List</h5>
                    </div>
                    <div className="col-sm-auto">
                      <button
                        type="button"
                        className="btn text-white add-btn"
                        style={{ backgroundColor: "#28a8df" }}
                        onClick={addBanner}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> Add Subscription Banner
                      </button>
                    </div>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  <TableContainer
                    columns={columns}
                    data={banners || []}
                    isGlobalFilter={true}
                    customPageSize={10}
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-bordered align-top table-nowrap mb-0"
                    SearchPlaceholder="Search Banner..."
                  />

                  <Modal isOpen={modal} toggle={toggle} centered>
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {!!isEdit ? "Edit Subscription Banner" : "Add Subscription Banner"}
                    </ModalHeader>
                    <Form
                      className="tablelist-form"
                      onSubmit={(e: any) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <ModalBody>
                        <div className="mb-3 text-center">
                          {previewImage ? (
                            <div className="position-relative d-inline-block w-100">
                              <img
                                src={previewImage}
                                alt="Banner Preview"
                                className="img-fluid rounded border shadow-sm"
                                style={{ maxHeight: "150px", width: "100%", objectFit: "cover" }}
                              />
                              <div className="mt-3 text-start bg-light p-2 rounded border small">
                                <Row className="g-2">
                                  <Col sm={6}>
                                    <p className="mb-1 text-truncate"><strong>Name:</strong> {selectedFile?.name || "Existing"}</p>
                                    <p className="mb-1"><strong>Size:</strong> {selectedFile ? formatBytes(selectedFile.size) : "N/A"}</p>
                                  </Col>
                                  <Col sm={6}>
                                    <p className="mb-1"><strong>Type:</strong> {selectedFile?.type || "image/jpeg"}</p>
                                    <p className="mb-0"><strong>Requirement:</strong> 1512 x 512 px</p>
                                  </Col>
                                </Row>
                              </div>
                            </div>
                          ) : (
                            <div className="border border-dashed rounded p-4 bg-light">
                              <i className="ri-image-add-line display-4 text-muted"></i>
                              <h5 className="mt-2 fs-14">No Banner Selected</h5>
                              <p className="text-muted small mb-0">Required size: 1512 x 512 px</p>
                            </div>
                          )}

                          <div className="mt-3 d-flex justify-content-center gap-2">
                            <Label htmlFor="banner-image-input" className="btn btn-primary btn-sm mb-0 cursor-pointer">
                              <i className="ri-upload-2-fill me-1"></i>
                              {previewImage ? "Change Banner" : "Upload Banner"}
                            </Label>
                            {previewImage && previewImage !== dummyImg && (
                              <Button
                                size="sm"
                                color="info"
                                className="text-white"
                                onClick={() => {
                                  setImageToCrop(previewImage);
                                  setCropModal(true);
                                }}
                              >
                                <i className="ri-crop-2-line me-1"></i> Crop
                              </Button>
                            )}
                            <Input
                              name="image"
                              className="form-control d-none"
                              id="banner-image-input"
                              type="file"
                              accept="image/png, image/jpeg, image/webp"
                              onChange={handleImageChange}
                            />
                          </div>

                          {dimensionError ? (
                            <div className="text-danger small mt-2">{dimensionError}</div>
                          ) : validation.touched.image && validation.errors.image ? (
                            <div className="text-danger small mt-2">{validation.errors.image}</div>
                          ) : null}
                          
                          <div className="mt-3 text-start border-top pt-2">
                            <p className="text-muted small mb-0">
                              <i className="ri-information-line align-middle me-1 text-info"></i>
                              This widescreen image will be used as a promotional banner for subscriptions.
                            </p>
                          </div>
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <Button color="light" onClick={toggle}>Cancel</Button>
                        <Button type="submit" style={{ backgroundColor: "#28a8df", borderColor: "#28a8df" }} className="text-white">
                          {isEdit ? "Update Banner" : "Add Banner"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  </Modal>

                  <Modal isOpen={statusModalOpen} toggle={toggleStatusModal} centered>
                    <ModalHeader toggle={toggleStatusModal}>Confirm Status Change</ModalHeader>
                    <ModalBody>
                      Are you sure you want to <strong>{pendingStatusData?.action}</strong> this subscription banner?
                    </ModalBody>
                    <ModalFooter>
                      <Button color="light" onClick={toggleStatusModal}>Cancel</Button>
                      <Button 
                        style={{ backgroundColor: "#28a8df", borderColor: "#28a8df" }} 
                        className="text-white"
                        onClick={confirmStatusUpdate}
                      >
                        Confirm
                      </Button>
                    </ModalFooter>
                  </Modal>

                  <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal} centered>
                    <ModalHeader toggle={toggleDeleteModal}>Confirm Deletion</ModalHeader>
                    <ModalBody>
                      Are you sure you want to delete this subscription banner?
                    </ModalBody>
                    <ModalFooter>
                      <Button color="light" onClick={toggleDeleteModal}>Cancel</Button>
                      <Button color="danger" onClick={() => confirmDelete(bannerToDeleteId)}>Delete</Button>
                    </ModalFooter>
                  </Modal>

                  <CropModal
                    isOpen={cropModal}
                    toggle={() => setCropModal(false)}
                    imageSrc={imageToCrop}
                    onCropSave={handleCropSave}
                    targetWidth={1512}
                    targetHeight={512}
                    title="Subscription Banner Crop"
                  />
                </div>
              </Card>
            </Col>
          </Row>
          <ToastContainer closeButton={false} limit={1} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SubscriptionBanner;
